import React, { useContext, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AuthContext, { isTokenExpired } from '../../contexts/authContext';
import { dashboardPagesMenu, demoPagesMenu } from '../../menu';

/**
 * Handles auth-based redirects:
 * - "/" (root): if NOT logged in → show home page (BO form). If logged in → redirect to dashboard.
 * - Public pages when not logged in: "/", "/bo-greetings", account guides, login, 404.
 * - If token is expired: clear auth and redirect to login.
 * - If logged in and on "/" or login page: redirect to dashboard.
 */
const AuthGuard = () => {
	const { authData, setAuthData } = useContext(AuthContext);
	const location = useLocation();
	const navigate = useNavigate();
	const loginPath = demoPagesMenu.login.path;
	const page404Path = demoPagesMenu.page404.path;
	const dashboardPath = dashboardPagesMenu.dashboard.path;
	const homePath = dashboardPagesMenu.wizard.path;

	useEffect(() => {
		const hasToken = !!authData?.token;

		// 1. Not logged in: allow public routes only. Redirect other routes to home (BO form).
		if (!hasToken) {
			const isAllowed =
				location.pathname === homePath ||
				location.pathname === dashboardPagesMenu.bogreetings.path ||
				location.pathname === dashboardPagesMenu.individualNote.path ||
				location.pathname === dashboardPagesMenu.jointNote.path ||
				location.pathname === loginPath ||
				location.pathname === `/${page404Path}`;
			if (!isAllowed) {
				navigate(homePath, { replace: true });
			}
			return;
		}

		// 2. Logged in but token expired: clear and send to login
		const expired = isTokenExpired(authData.token.accessTokenExpiry);
		if (expired) {
			if (setAuthData) setAuthData(null);
			navigate(loginPath, { replace: true });
			return;
		}

		// 3. Logged in with valid token: "/" or login page → redirect to dashboard
		if (location.pathname === homePath || location.pathname === loginPath) {
			navigate(dashboardPath, { replace: true });
		}
	}, [authData, location.pathname, loginPath, page404Path, dashboardPath, homePath, navigate, setAuthData]);

	return null;
};

export default AuthGuard;
